import styles from "./input-field.module.less";
import PropTypes from "prop-types";
import { AnimatePresence } from "framer-motion";
import { motion } from "polyfill/motion-polyfill/motion-polyfill";

const translations = {
  optional: "(frivillig)",
};

const InputField = (props) => {
  let { name, value, isValid, error, isEditable, isDisabled } = props;

  if (props.formField) {
    name = props.formField.name;
    value = props.formField.value;
    isValid = props.formField.isValid;
    error = props.formField.error;
    isEditable = props.formField.isEditable;
  }

  if (isEditable === false) {
    isDisabled = true;
  }

  const displayError = () => {
    return isValid === false;
  };

  const isSuccess = () => {
    return value && isValid === true && !props.isDisabled;
  };

  const onBlur = (event) => {
    if (props.onBlur && typeof props.onBlur === "function") {
      props.onBlur(event);
    }
  };
  /** Native input onKeyUp */
  const onKeyUp = (event) => {
    if (props.onKeyUp && typeof props.onKeyUp === "function") {
      props.onKeyUp(event);
    }
  };
  /** Native input onKeyDown */
  const onKeyDown = (event) => {
    if (props.onKeyDown && typeof props.onKeyDown === "function") {
      props.onKeyDown(event);
    }
  };

  return (
    <div
      className={[
        styles.wrapper,
        props.fieldTheme,
        displayError() ? styles.wrapperError : "",
        isSuccess() ? styles.wrapperSuccess : "",
        props.className,
        isDisabled ? styles.wrapperDisabled : "",
      ].join(" ")}
    >
      <div className={styles.innerWrapper}>
        {props.label && (
          <label htmlFor={name ? name : undefined} className={styles.label}>
            {props.label}
            {props.additionalText && <span className={styles.additionalText}>({props.additionalText})</span>}
          </label>
        )}
        <div className={styles.inputWrapper}>
          <input
            id={name ? name : undefined}
            type={props.type}
            inputMode={props.type === "number" ? "numeric" : undefined}
            tabIndex={0}
            className={[styles.input, props.inputClassName].join(" ")}
            value={value ? value : ""}
            name={name}
            pattern={props.pattern}
            disabled={isDisabled}
            autoFocus={props.focus ? props.focus : undefined}
            placeholder={props.placeholder}
            autoComplete={props.autoComplete}
            onChange={(event) => props.onChange(event.target.name, event.target.value)}
            onBlur={(event) => onBlur(event)}
            onKeyUp={(event) => onKeyUp(event)}
            onKeyDown={(event) => onKeyDown(event)}
            minLength={props.minLength ? props.minLength : undefined}
            maxLength={props.maxLength ? props.maxLength : undefined}
          />
          <div className={styles.borderBottom} />
          <AnimatePresence>
            {displayError() && (
              <motion.div
                key="errorIcon"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ type: "tween" }}
                className={`${styles.icon} ${styles.errorIcon} icon-error`}
              ></motion.div>
            )}
            {isSuccess() && (
              <motion.div
                key="successIcon"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ type: "tween" }}
                className={`${styles.icon} ${styles.successIcon} icon-check`}
              ></motion.div>
            )}
            {isDisabled && (
              <motion.div
                key="successIcon"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.5 }}
                exit={{ opacity: 0 }}
                transition={{ type: "tween" }}
                className={`${styles.icon} ${styles.disabledIcon} icon-lock-filled`}
              ></motion.div>
            )}
          </AnimatePresence>
        </div>
        <AnimatePresence>
          {displayError() && (
            <motion.div
              key="errorText"
              className={styles.errorWrapper}
              initial={{ opacity: 0, y: "10%" }}
              animate={{ opacity: 1, y: "0%" }}
              exit={{ opacity: 0, y: "10%" }}
            >
              {error && <span className={styles.errorText}>{error}</span>}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

InputField.propTypes = {
  type: PropTypes.oneOf(["text", "number", "date"]),
  value: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  focus: PropTypes.bool,
  autoComplete: PropTypes.string,
  errorHeading: PropTypes.string,
  errorText: PropTypes.string,
  isValid: PropTypes.bool,
  pattern: PropTypes.string,
  isDisabled: PropTypes.bool,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  formField: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string,
    isValid: PropTypes.bool,
    error: PropTypes.string,
    isEditable: PropTypes.bool,
  }),
  onBlur: PropTypes.func,
  onKeyUp: PropTypes.func,
  onKeyDown: PropTypes.func,
  fieldTheme: PropTypes.oneOf(["light", "standard"]),
  additionalText: PropTypes.string,
};

InputField.defaultProps = {
  value: "",
  name: "",
  label: "",
  placeholder: "",
  onChange: () => {},
  focus: false,
  autoComplete: "off",
  isValid: null,
  pattern: null,
  isDisabled: false,
  minLength: null,
  maxLength: null,
  type: "text",
  description: null,
  showOptionalText: false,
  isEditable: true,
  fieldTheme: "standard",
};

export default InputField;
